import React from "react";
import { useNavigate } from "react-router-dom";
import { Col, Badge } from "reactstrap";
import { MdRestore, MdVisibility } from "react-icons/md";
import ButtonSubmit from "../../../components/Buttons/ButtonStyle";
import ImagePlaceHolder from "../../../assets/images/placeholder/ImagePlaceHolder.png";
import LoaderEventItem from "../../../components/Loader/LoaderEventItem";
import PropTypes from "prop-types";

const AllEventPersonnelItems = ({
  userEmail,
  filteredEvents,
  isLoadingGetEvents,
  showArchives = false,  // ✅ New prop: are we viewing archives?
  onRestore,              // ✅ New prop: restore handler function
  restoringEventId,       // ✅ New prop: ID of event being restored
  isRestoring             // ✅ New prop: is restore in progress?
}) => {
  const Navigate = useNavigate();
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const checkForRole = (personnelArray, targetEmail) => {
    let roles = {
      isOrganizer: false,
      isCoOrganizer: false,
      isMarketing: false,
      isSales: false,
      isFinance: false,
      isHost: false,
      isCoordinator: false,
      isExhibitor: false,
      isExhibitorAssistance: false,
      isSpeaker: false,
      isSecurity: false,
    };

    personnelArray?.forEach((person) => {
      if (person?.email === targetEmail) {
        if (person?.role === "event_organizer") roles.isOrganizer = true;
        else if (person?.role === "co_event_organizer") roles.isCoOrganizer = true;
        else if (person?.role === "event_org_marketing") roles.isMarketing = true;
        else if (person?.role === "event_org_sales") roles.isSales = true;
        else if (person?.role === "event_org_finance") roles.isFinance = true;
        else if (person?.role === "host") roles.isHost = true;
        else if (person?.role === "session_coordinator") roles.isCoordinator = true;
        else if (person?.role === "exhibitor") roles.isExhibitor = true;
        else if (person?.role === "exhibitor_assistant") roles.isExhibitorAssistance = true;
        else if (person?.role === "speaker") roles.isSpeaker = true;
        else if (person?.role === "securityGuard") roles.isSecurity = true;
      }
    });

    return roles;
  };

  const handleAttendeeEnterEvent = (eventName, evID) => {
    Navigate(`/${eventName?.replace(/ /g, "-")}/event-landing/${evID}`);
  };

  const handleEditEvent = (eventName, evID) => {
    Navigate(`/${eventName?.replace(/ /g, "-")}/set/${evID}`);
  };

  const handleSetBooth = (eventName, evID) => {
    Navigate(`/${eventName?.replace(/ /g, "-")}/set-booth/${evID}`);
  };

  // ✅ Handle restore click
  const handleRestoreClick = (eventId, eventName) => {
    if (window.confirm(`Are you sure you want to restore "${eventName}"?`)) {
      onRestore(eventId);
    }
  };

  if (isLoadingGetEvents) {
    return <LoaderEventItem />;
  }

  if (!filteredEvents || filteredEvents.length === 0) {
    return (
      <Col xs={12}>
        <div className="text-center py-5 text-muted">
          <p className="mb-0">
            {showArchives 
              ? "No archived events found" 
              : "No events found matching your search"}
          </p>
        </div>
      </Col>
    );
  }

  return (
    <>
      {filteredEvents?.map((item, index) => {
        // Format the event's start date
        let dateValue = new Date(item.startDate);
        let day = dayNames[dateValue.getUTCDay()];
        let month = monthNames[dateValue.getUTCMonth()];
        let year = dateValue.getUTCFullYear();
        let date = dateValue.getUTCDate();
        let formattedDate = `${day} ${date} ${month} ${year}`;

        const {
          isOrganizer,
          isCoOrganizer,
          isMarketing,
          isSales,
          isFinance,
          isCoordinator,
          isExhibitor,
          isExhibitorAssistance,
          isSpeaker,
          isHost,
          isSecurity,
        } = checkForRole(item?.personnel, userEmail);

        return (
          <Col xs={12} sm={12} md={12} lg={12} key={item._id || index}>
            <div className="d-flex p-3 mb-4 shadow-sm bg-white card-brd myEvent-card-container position-relative">
              
              {/* ✅ Archived Badge */}
              {showArchives && (
                <Badge 
                  color="warning" 
                  className="position-absolute top-0 end-0 m-2"
                  style={{ 
                    fontSize: '10px', 
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <MdRestore size={12} />
                  Archived
                </Badge>
              )}

              <div className="d-flex align-items-center" style={{ flex: 0.4 }}>
                <div className="MyEvent_Parent_div d-flex pt-3">
                  <div className="MyEvent_image_div">
                    <img
                      src={item?.bannerImage ? item?.bannerImage : ImagePlaceHolder}
                      width="100%"
                      height="100%"
                      className="card-brd"
                      alt="event"
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="w-100 mx-2 d-flex flex-column">
                <div style={{ fontSize: 16, fontWeight: "600", color: "black" }}>
                  {item?.eventName}
                </div>
                
                <div className="mt-2" style={{ fontSize: 13 }}>
                  {item?.eventDescription && item?.eventDescription.length > 35
                    ? `${item?.eventDescription.substring(0, 200)}...`
                    : item?.eventDescription}
                </div>
                
                <div style={{ fontSize: 14, fontWeight: "600", color: "black" }}>
                  {formattedDate} | {`${item.startTime} - ${item.endTime} SAST`}
                </div>

                {/* ✅ Show archived date if available */}
                {showArchives && item.deletedAt && (
                  <div className="mt-1" style={{ fontSize: 12, color: '#6c757d' }}>
                    Archived: {new Date(item.deletedAt).toLocaleDateString()}
                  </div>
                )}
                
                <div className="mt-2 mb-1 d-flex flex-wrap gap-2">
                  {showArchives ? (
                    <>
                      {/* ✅ Restore Button for Archived Events */}
                      <button
                        onClick={() => handleRestoreClick(item._id, item.eventName)}
                        disabled={isRestoring && restoringEventId === item._id}
                        className="btn btn-success btn-sm d-flex align-items-center gap-1"
                        style={{ 
                          minHeight: '36px',
                          fontSize: '13px',
                          padding: '6px 12px'
                        }}
                      >
                        <MdRestore size={16} />
                        {isRestoring && restoringEventId === item._id 
                          ? 'Restoring...' 
                          : 'Restore'}
                      </button>

                      {/* ✅ View Button for Archived Events */}
                      <button
                        onClick={() => handleAttendeeEnterEvent(item?.eventName, item?._id)}
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        style={{ 
                          minHeight: '36px',
                          fontSize: '13px',
                          padding: '6px 12px'
                        }}
                      >
                        <MdVisibility size={16} />
                        View
                      </button>
                    </>
                  ) : (
                    <>
                      {/* ✅ Standard Buttons for Active Events */}
                      <ButtonSubmit
                        Title="Visit Event"
                        ColorText="#F68A20"
                        BorderColor="#F68A20"
                        BackgroundColor="white"
                        borderRadius="25px"
                        handleOnclick={() =>
                          handleAttendeeEnterEvent(item?.eventName, item?._id)
                        }
                      />
                      
                      {(isOrganizer || isCoOrganizer || isMarketing || isSales || isFinance || isHost || isCoordinator) && (
                        <div
                          style={{
                            fontSize: 14,
                            textDecoration: "underline",
                            color: "#0e35dd",
                            fontWeight: "500",
                            cursor: "pointer",
                            marginLeft: '10px',
                          }}
                          onClick={() => handleEditEvent(item?.eventName, item?._id)}
                        >
                          Edit Event
                        </div>
                      )}
                      
                      {(isExhibitor || isExhibitorAssistance) && (
                        <div
                          style={{
                            fontSize: 14,
                            textDecoration: "underline",
                            color: "#0e35dd",
                            fontWeight: "500",
                            cursor: "pointer",
                            marginLeft: '10px',
                          }}
                          onClick={() => handleSetBooth(item?.eventName, item?._id)}
                        >
                          Set Up Booth
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Col>
        );
      })}
    </>
  );
};

AllEventPersonnelItems.propTypes = {
  userEmail: PropTypes.string.isRequired,
  filteredEvents: PropTypes.array.isRequired,
  isLoadingGetEvents: PropTypes.bool.isRequired,
  showArchives: PropTypes.bool.isRequired,  // ✅ PropTypes
  onRestore: PropTypes.func.isRequired,      // ✅ PropTypes
  restoringEventId: PropTypes.string,        // ✅ PropTypes
  isRestoring: PropTypes.bool.isRequired,    // ✅ PropTypes
};

export default AllEventPersonnelItems;